import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Stethoscope, Zap, DollarSign, Palette, BarChart3, Brain, Loader2 } from "lucide-react";
import { Layout } from "@/components/layout";
import { useMetaAccount } from "@/contexts/MetaAccountContext";
import { useDateRange } from "@/contexts/DateRangeContext";
import { DateRangePicker } from "@/components/meta/DateRangePicker";
import { AnalysisCard } from "@/components/meta/AnalysisCard";
import type { MetaAiAnalysis } from "@shared/schema";

const analysisTypes = [
  { type: "health_check", icon: Stethoscope, title: "Health Check", description: "Full account diagnosis with grade and recommendations" },
  { type: "anomaly_detection", icon: Zap, title: "Anomaly Detection", description: "Spot sudden metric shifts and unusual patterns" },
  { type: "budget_recommendation", icon: DollarSign, title: "Budget Optimizer", description: "Find where to reallocate budget for better ROI" },
  { type: "creative_performance", icon: Palette, title: "Creative Performance", description: "Best and worst performing ad creatives" },
  { type: "weekly_summary", icon: BarChart3, title: "Weekly Summary", description: "Executive brief of this period's performance" },
];

export default function MetaInsightsPage() {
  const { selectedAccountId } = useMetaAccount();
  const { dateRange, formatDate } = useDateRange();
  const queryClient = useQueryClient();

  const dateFrom = formatDate(dateRange.from);
  const dateTo = formatDate(dateRange.to);

  const { data: analyses = [], isLoading } = useQuery<MetaAiAnalysis[]>({
    queryKey: ["/api/meta/accounts", selectedAccountId, "analyses"],
    queryFn: async () => {
      if (!selectedAccountId) return [];
      const res = await fetch(`/api/meta/accounts/${selectedAccountId}/analyses?limit=20`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!selectedAccountId,
  });

  const analyzeMutation = useMutation({
    mutationFn: async (type: string) => {
      if (!selectedAccountId) throw new Error("No account selected");
      const res = await fetch(`/api/meta/accounts/${selectedAccountId}/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, dateFrom, dateTo }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Analysis failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meta/accounts", selectedAccountId, "analyses"] });
    },
  });

  if (!selectedAccountId) {
    return (
      <Layout>
      <div className="p-6" data-testid="meta-insights-no-account">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">AI Insights</h1>
        <Card className="p-12">
          <div className="flex flex-col items-center text-center">
            <Brain className="h-12 w-12 text-gray-300 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Select an account</h2>
            <p className="text-gray-500">Choose an ad account from the sidebar to run AI analyses.</p>
          </div>
        </Card>
      </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="p-6" data-testid="meta-insights-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">AI Insights</h1>
        <DateRangePicker />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {analysisTypes.map(({ type, icon: Icon, title, description }) => {
          const isRunning = analyzeMutation.isPending && analyzeMutation.variables === type;
          return (
            <Card key={type} className="hover:shadow-md transition-shadow" data-testid={`analysis-trigger-${type}`}>
              <CardContent className="p-5 flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mb-3">
                  <Icon className="h-6 w-6 text-indigo-500" />
                </div>
                <h3 className="font-semibold text-sm text-gray-900 mb-1">{title}</h3>
                <p className="text-xs text-gray-500 mb-4">{description}</p>
                <Button
                  size="sm"
                  className="w-full bg-indigo-500 hover:bg-indigo-600"
                  disabled={analyzeMutation.isPending}
                  onClick={() => analyzeMutation.mutate(type)}
                  data-testid={`run-analysis-${type}`}
                >
                  {isRunning ? (
                    <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Analyzing...</>
                  ) : (
                    "Run"
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Analyses</h2>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : analyses.length === 0 ? (
          <Card className="p-8">
            <div className="flex flex-col items-center text-center">
              <Brain className="h-8 w-8 text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">No analyses yet. Run one above to get started.</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {analyses.map(a => <AnalysisCard key={a.id} analysis={a} />)}
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
}
